import { useCallback, useEffect, useMemo, useState } from "react";
import type { AttendanceReport, Kennel } from "@hash/shared";
import { eventLabel } from "@hash/shared";
import { api } from "../api/client";
import { getClaims } from "../auth";
import { formatMoney } from "../lib/checkInStatus";
import { localDate, formatCalendarDate } from "../lib/trailDate";

/**
 * Attendance + payment reporting for admins and misman (Story 9.3). Pick a set
 * of kennels and an inclusive date window; the report summarizes the events —
 * trail count, average attendance, unique attendees, money collected, active
 * members vs. not — and lists every event in chronological order with its
 * attendance and money counts.
 *
 * Kennel scope follows the role: an admin sees every kennel, a misman only the
 * kennels they manage (the api enforces the same gate).
 */
export function ReportsPage() {
  // Resolve the claims once. getClaims() returns a fresh object each call, so
  // reading it inline would give loadKennels a new identity every render and the
  // load effect would re-run and reset the selection on every keystroke/tap.
  const claims = useMemo(() => getClaims(), []);
  const isAdmin = claims?.role === "admin";
  const mismanKennels = useMemo(
    () => new Set(claims?.mismanKennels ?? []),
    [claims]
  );

  const [kennels, setKennels] = useState<Kennel[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Default to the trailing 90 days, the window a misman most often reconciles.
  const [from, setFrom] = useState(() => localDate(-90));
  const [to, setTo] = useState(() => localDate(0));

  const [report, setReport] = useState<AttendanceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reportable kennels are always managed (reference kennels have no trails to
  // report on): every managed kennel for an admin, otherwise only the managed
  // kennels the caller is misman of.
  const loadKennels = useCallback(async () => {
    const { kennels: all } = await api.kennels.list({ limit: 200 });
    const reportable = all.filter(
      (k) => k.managed && (isAdmin || mismanKennels.has(k.id))
    );
    setKennels(reportable);
    // Pre-select everything in scope so a single tap on "Generate" works.
    setSelected(new Set(reportable.map((k) => k.id)));
  }, [isAdmin, mismanKennels]);

  useEffect(() => {
    loadKennels().catch((e) =>
      setError(e instanceof Error ? e.message : "Failed to load kennels")
    );
  }, [loadKennels]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.reports.attendance([...selected], from, to);
      setReport(res);
    } catch (e) {
      setReport(null);
      setError(e instanceof Error ? e.message : "Failed to generate report");
    } finally {
      setLoading(false);
    }
  }, [selected, from, to]);

  const canGenerate = selected.size > 0 && from <= to && !loading;

  return (
    <div className="min-h-screen pb-24">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3">
        <h2 className="text-xl font-bold text-slate-900">Reports</h2>
        <p className="mt-1 text-sm text-slate-500">
          Attendance and money collected for your kennels over a date range.
        </p>
      </header>

      <section className="px-4 py-4 space-y-4">
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-2">Kennels</h3>
          {kennels.length === 0 ? (
            <p className="text-sm text-slate-500">
              No kennels available to report on.
            </p>
          ) : (
            <ul className="space-y-1">
              {kennels.map((k) => (
                <li key={k.id}>
                  <label className="flex items-center gap-3 px-3 py-2 min-h-[44px] rounded-lg border border-slate-200 active:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={selected.has(k.id)}
                      onChange={() => toggle(k.id)}
                      className="h-5 w-5"
                    />
                    <span className="text-slate-900">
                      {k.shortName ?? k.name}
                    </span>
                    {k.shortName && (
                      <span className="text-xs text-slate-400">{k.name}</span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex gap-3">
          <label className="flex-1 text-sm">
            <span className="block text-slate-700 mb-1">From</span>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-300"
            />
          </label>
          <label className="flex-1 text-sm">
            <span className="block text-slate-700 mb-1">To</span>
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-300"
            />
          </label>
        </div>

        <button
          onClick={generate}
          disabled={!canGenerate}
          className="w-full h-12 rounded-lg bg-slate-900 text-white font-medium active:bg-slate-700 disabled:opacity-50"
        >
          {loading ? "Generating…" : "Generate report"}
        </button>
      </section>

      {error && <p className="px-4 py-3 text-sm text-red-600">{error}</p>}

      {report && !loading && <ReportView report={report} />}
    </div>
  );
}

function ReportView({ report }: { report: AttendanceReport }) {
  if (report.trailCount === 0) {
    return (
      <p className="px-4 py-6 text-sm text-slate-500">
        No trails for the selected kennels in this window.
      </p>
    );
  }
  return (
    <section className="px-4 pb-6">
      <dl className="grid grid-cols-2 gap-3">
        <Stat label="Trails" value={String(report.trailCount)} />
        <Stat
          label="Avg attendance / trail"
          value={report.avgAttendancePerTrail.toFixed(1)}
        />
        <Stat label="Unique attendees" value={String(report.uniqueAttendees)} />
        <Stat
          label="Active members / not"
          value={`${report.activeMembers} / ${report.nonMembers}`}
        />
        <Stat
          label="Total collected"
          value={formatMoney(report.totalMoneyCollected)}
        />
        <Stat
          label="Avg collected / trail"
          value={formatMoney(Math.round(report.avgMoneyPerTrail))}
        />
      </dl>

      <h3 className="mt-6 mb-2 text-sm font-medium text-slate-700">
        Trails ({report.trailCount})
      </h3>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {report.trails.map((t) => (
          <li
            key={t.trailId}
            className="flex items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0">
              <div className="text-slate-900 truncate">
                <span className="font-medium">{t.kennel}</span>{" "}
                {eventLabel(t)}
              </div>
              <div className="text-sm text-slate-500">
                {formatCalendarDate(t.date)}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-slate-900">{t.attendance} in</div>
              <div className="text-sm text-slate-500">
                {formatMoney(t.moneyCollected)}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-lg font-semibold text-slate-900">{value}</dd>
    </div>
  );
}
